import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './BBMappingPage.css'; // Reuse same styles as other mapping pages

/**
 * ItemOptionSpaceMappingPage - Admin tool for scoping item_options within the item's assigned spaces.
 *
 * Scoping rule: every master_item is already assigned to a set of master_spaces via
 * `reference_suggested_items`. This page lets the admin narrow each option to a SUBSET of
 * that item's assigned spaces — i.e., the admin can only DESELECT from the item's assigned
 * spaces, never add new ones. Items assigned to <2 spaces are hidden (nothing to narrow).
 *
 * Data flow:
 *   reference_suggested_items                               ← defines item→spaces membership
 *   master_items.item_options.options[].allowed_space_ids  ← the source of truth for scoping
 *   item_option_space_suggestions                           ← interim AI/manual staging
 *
 * Empty / missing allowed_space_ids ⇒ option shows in ALL of the item's assigned spaces.
 *
 * Access via /admin/item-option-spaces (no sidebar link).
 */
const ItemOptionSpaceMappingPage = () => {
  // ─── State ──────────────────────────────────────────────────────

  const [masterItems, setMasterItems] = useState([]);
  const [masterSpaces, setMasterSpaces] = useState([]);
  const [itemSpaceLinks, setItemSpaceLinks] = useState([]); // reference_suggested_items rows
  const [suggestions, setSuggestions] = useState({}); // keyed by `${itemId}::${group}::${name}`
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState('work'); // work, all, unscoped, pending, scoped, low-confidence
  const [searchTerm, setSearchTerm] = useState('');
  const [apiKey] = useState(process.env.REACT_APP_CLAUDE_API_KEY || '');
  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });
  const [saving, setSaving] = useState(false);

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, spacesRes, linksRes, suggestionsRes] = await Promise.all([
        supabase
          .from('master_items')
          .select('id, item_name, item_options, is_bundle')
          .order('item_name'),
        supabase.from('master_spaces').select('id, name').order('name'),
        supabase.from('reference_suggested_items').select('master_item_id, master_space_id'),
        supabase.from('item_option_space_suggestions').select('*'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (spacesRes.error) throw spacesRes.error;
      if (linksRes.error) throw linksRes.error;
      if (suggestionsRes.error) throw suggestionsRes.error;

      setMasterItems(itemsRes.data || []);
      setMasterSpaces(spacesRes.data || []);
      setItemSpaceLinks(linksRes.data || []);

      // Index suggestions by composite key
      const sugMap = {};
      (suggestionsRes.data || []).forEach(s => {
        const key = `${s.master_item_id}::${s.option_group || ''}::${s.option_name}`;
        sugMap[key] = s;
      });
      setSuggestions(sugMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Lookups ─────────────────────────────────────────────────────

  const spaceMap = useMemo(() => {
    const m = {};
    masterSpaces.forEach(s => {
      m[s.id] = s.name;
    });
    return m;
  }, [masterSpaces]);

  // Map of master_item_id → array of assigned master_space_ids (from reference_suggested_items)
  const itemSpaceMap = useMemo(() => {
    const m = {};
    itemSpaceLinks.forEach(link => {
      if (!link.master_item_id || !link.master_space_id) return;
      if (!m[link.master_item_id]) m[link.master_item_id] = [];
      if (!m[link.master_item_id].includes(link.master_space_id)) {
        m[link.master_item_id].push(link.master_space_id);
      }
    });
    return m;
  }, [itemSpaceLinks]);

  // ─── Flatten items → one row per option ─────────────────────────
  // Only items assigned to ≥2 spaces are included (nothing to narrow otherwise).

  const allOptionRows = useMemo(() => {
    const rows = [];
    masterItems.forEach(item => {
      const options = item.item_options?.options;
      if (!Array.isArray(options) || options.length === 0) return;

      const assignedSpaceIds = itemSpaceMap[item.id] || [];
      if (assignedSpaceIds.length < 2) return; // skip items with 0 or 1 assigned spaces

      options.forEach(opt => {
        const group = opt.group || '';
        const key = `${item.id}::${group}::${opt.name}`;
        const currentAllowed = Array.isArray(opt.allowed_space_ids) ? opt.allowed_space_ids : [];
        rows.push({
          key,
          master_item_id: item.id,
          item_name: item.item_name,
          is_bundle: item.is_bundle,
          option_group: group,
          option_name: opt.name,
          option_data: opt,
          current_allowed_space_ids: currentAllowed,
          assigned_space_ids: assignedSpaceIds,
        });
      });
    });
    return rows;
  }, [masterItems, itemSpaceMap]);

  // ─── Helpers: read/write current "mapped" allowed_space_ids ─────

  // Get the effective allowed_space_ids for a row — suggestions override current
  const getEffectiveAllowedSpaces = useCallback(
    row => {
      const s = suggestions[row.key];
      if (s && (s.status === 'approved' || s.status === 'manual')) {
        return Array.isArray(s.suggested_space_ids) ? s.suggested_space_ids : [];
      }
      return row.current_allowed_space_ids;
    },
    [suggestions],
  );

  // A row is "scoped" (narrowed) if the effective allowed_space_ids is a non-empty
  // proper subset of the item's assigned spaces. Empty ⇒ applies to all assigned spaces.
  const isScoped = useCallback(
    row => {
      const eff = getEffectiveAllowedSpaces(row);
      if (!Array.isArray(eff) || eff.length === 0) return false;
      return eff.length < (row.assigned_space_ids?.length || 0);
    },
    [getEffectiveAllowedSpaces],
  );

  // ─── Stats ───────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = allOptionRows.length;
    const scoped = allOptionRows.filter(r => isScoped(r)).length;
    const unscoped = total - scoped;
    const pending = Object.values(suggestions).filter(s => s.status === 'pending').length;
    const approved = Object.values(suggestions).filter(
      s => s.status === 'approved' || s.status === 'manual',
    ).length;
    // Working set: rows that are unscoped AND have no suggestion yet
    const workRows = allOptionRows.filter(r => !isScoped(r) && !suggestions[r.key]).length;
    return { total, scoped, unscoped, pending, approved, workRows };
  }, [allOptionRows, suggestions, isScoped]);

  // ─── Filtering ───────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    let rows = allOptionRows;

    switch (filter) {
      case 'work':
        rows = rows.filter(r => !isScoped(r) && !suggestions[r.key]);
        break;
      case 'unscoped':
        rows = rows.filter(r => !isScoped(r));
        break;
      case 'pending':
        rows = rows.filter(r => suggestions[r.key]?.status === 'pending');
        break;
      case 'scoped':
        rows = rows.filter(r => isScoped(r));
        break;
      case 'low-confidence':
        rows = rows.filter(r => {
          const s = suggestions[r.key];
          return s && s.confidence != null && s.confidence < 0.8;
        });
        break;
      default:
        break;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(
        r =>
          r.item_name.toLowerCase().includes(term) ||
          r.option_name.toLowerCase().includes(term) ||
          (r.option_group || '').toLowerCase().includes(term),
      );
    }

    return rows;
  }, [allOptionRows, filter, searchTerm, suggestions, isScoped]);

  // ─── Actions: suggestions table ─────────────────────────────────

  const upsertSuggestion = async payload => {
    const { error } = await supabase
      .from('item_option_space_suggestions')
      .upsert(payload, { onConflict: 'master_item_id,option_group,option_name' });
    if (error) throw error;
  };

  const updateSuggestionStatus = async (row, status) => {
    const { error } = await supabase
      .from('item_option_space_suggestions')
      .update({ status })
      .eq('master_item_id', row.master_item_id)
      .eq('option_group', row.option_group || '')
      .eq('option_name', row.option_name);
    if (error) throw error;
  };

  const handleApprove = async row => {
    const s = suggestions[row.key];
    if (!s) return;
    try {
      await updateSuggestionStatus(row, 'approved');
      setSuggestions(prev => ({
        ...prev,
        [row.key]: { ...prev[row.key], status: 'approved' },
      }));
    } catch (err) {
      alert('שגיאה באישור: ' + err.message);
    }
  };

  const handleReject = async row => {
    const s = suggestions[row.key];
    if (!s) return;
    try {
      await updateSuggestionStatus(row, 'rejected');
      setSuggestions(prev => ({
        ...prev,
        [row.key]: { ...prev[row.key], status: 'rejected' },
      }));
    } catch (err) {
      alert('שגיאה בדחייה: ' + err.message);
    }
  };

  const handleClear = async row => {
    try {
      const { error } = await supabase
        .from('item_option_space_suggestions')
        .delete()
        .eq('master_item_id', row.master_item_id)
        .eq('option_group', row.option_group || '')
        .eq('option_name', row.option_name);
      if (error) throw error;
      setSuggestions(prev => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
    } catch (err) {
      alert('שגיאה במחיקה: ' + err.message);
    }
  };

  // Toggle a single space in the manual selection (status = manual).
  // Admin can only narrow within the item's assigned spaces (reference_suggested_items).
  const handleToggleSpace = async (row, spaceId) => {
    // Safety: only allow toggling spaces the item is actually assigned to
    if (!row.assigned_space_ids.includes(spaceId)) return;

    const existing = suggestions[row.key];
    // Starting point: if nothing is scoped yet, treat as "all assigned spaces selected"
    const rawCurrent =
      existing && (existing.status === 'manual' || existing.status === 'approved')
        ? existing.suggested_space_ids || []
        : row.current_allowed_space_ids;

    const currentSelected =
      Array.isArray(rawCurrent) && rawCurrent.length > 0 ? rawCurrent : row.assigned_space_ids;

    let nextSelected = currentSelected.includes(spaceId)
      ? currentSelected.filter(id => id !== spaceId)
      : [...currentSelected, spaceId];

    // Clamp to the item's assigned spaces (guard against stale data)
    nextSelected = nextSelected.filter(id => row.assigned_space_ids.includes(id));

    // If admin re-selected every assigned space, normalize to empty (= "all assigned")
    if (nextSelected.length === row.assigned_space_ids.length) {
      nextSelected = [];
    }

    try {
      await upsertSuggestion({
        master_item_id: row.master_item_id,
        option_group: row.option_group || '',
        option_name: row.option_name,
        suggested_space_ids: nextSelected,
        status: 'manual',
        confidence: 1.0,
        ai_reasoning: 'Manual mapping by admin',
      });

      setSuggestions(prev => ({
        ...prev,
        [row.key]: {
          ...(prev[row.key] || {}),
          master_item_id: row.master_item_id,
          option_group: row.option_group || '',
          option_name: row.option_name,
          suggested_space_ids: nextSelected,
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: 'Manual mapping by admin',
        },
      }));
    } catch (err) {
      alert('שגיאה בשמירה: ' + err.message);
    }
  };

  // "Show in all of this item's assigned spaces" — empty allowed_space_ids = all-of-item
  const handleClearSpaces = async row => {
    try {
      await upsertSuggestion({
        master_item_id: row.master_item_id,
        option_group: row.option_group || '',
        option_name: row.option_name,
        suggested_space_ids: [],
        status: 'manual',
        confidence: 1.0,
        ai_reasoning: "Manual: show in all of item's assigned spaces",
      });

      setSuggestions(prev => ({
        ...prev,
        [row.key]: {
          ...(prev[row.key] || {}),
          master_item_id: row.master_item_id,
          option_group: row.option_group || '',
          option_name: row.option_name,
          suggested_space_ids: [],
          status: 'manual',
          confidence: 1.0,
          ai_reasoning: "Manual: show in all of item's assigned spaces",
        },
      }));
    } catch (err) {
      alert('שגיאה בשמירה: ' + err.message);
    }
  };

  // ─── Batch actions ──────────────────────────────────────────────

  const handleBatchApprove = async () => {
    const highConf = allOptionRows.filter(r => {
      const s = suggestions[r.key];
      return s && s.status === 'pending' && s.confidence != null && s.confidence >= 0.8;
    });
    if (highConf.length === 0) {
      alert('אין הצעות ברמת ביטחון גבוהה לאישור');
      return;
    }
    if (!window.confirm(`לאשר ${highConf.length} הצעות ברמת ביטחון גבוהה (≥0.8)?`)) return;

    for (const row of highConf) {
      // Inline approve to avoid per-call re-renders
      try {
        await updateSuggestionStatus(row, 'approved');
      } catch (err) {
        console.error(`Error approving ${row.item_name} / ${row.option_name}:`, err);
      }
    }
    await fetchData();
  };

  // Write all approved/manual suggestions back to master_items.item_options
  const handleBatchApply = async () => {
    // Group suggestions by master_item_id
    const byItem = {};
    allOptionRows.forEach(row => {
      const s = suggestions[row.key];
      if (!s) return;
      if (s.status !== 'approved' && s.status !== 'manual') return;

      // Skip if suggestion matches current value (no change)
      const cur = row.current_allowed_space_ids;
      const next = s.suggested_space_ids || [];
      if (arraysEqual(cur, next)) return;

      if (!byItem[row.master_item_id]) byItem[row.master_item_id] = [];
      byItem[row.master_item_id].push({ row, next });
    });

    const itemIds = Object.keys(byItem);
    if (itemIds.length === 0) {
      alert('אין שינויים להחלה');
      return;
    }

    const totalChanges = Object.values(byItem).reduce((sum, arr) => sum + arr.length, 0);
    if (!window.confirm(`להחיל ${totalChanges} שינויים על ${itemIds.length} פריטים?`)) return;

    setSaving(true);
    let appliedItems = 0;
    try {
      for (const itemId of itemIds) {
        const item = masterItems.find(i => i.id === itemId);
        if (!item) continue;

        // Deep clone current item_options
        const newOptions = JSON.parse(JSON.stringify(item.item_options || {}));
        if (!Array.isArray(newOptions.options)) continue;

        // Apply each change
        byItem[itemId].forEach(({ row, next }) => {
          const idx = newOptions.options.findIndex(
            o => (o.group || '') === row.option_group && o.name === row.option_name,
          );
          if (idx >= 0) {
            if (next.length === 0) {
              // Empty → remove the field entirely to keep JSONB clean
              delete newOptions.options[idx].allowed_space_ids;
            } else {
              newOptions.options[idx].allowed_space_ids = next;
            }
          }
        });

        const { error } = await supabase
          .from('master_items')
          .update({ item_options: newOptions })
          .eq('id', itemId);

        if (error) {
          console.error(`Error applying to ${item.item_name}:`, error);
          continue;
        }
        appliedItems++;
      }

      alert(`הוחלו שינויים ל־${appliedItems} פריטים`);
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  // ─── Claude AI ──────────────────────────────────────────────────

  const runAiMapping = async () => {
    if (!apiKey) {
      alert('נא להזין Claude API Key');
      return;
    }

    // Only process rows without existing suggestions and without current scoping
    const toProcess = allOptionRows.filter(r => !suggestions[r.key] && !isScoped(r));
    if (toProcess.length === 0) {
      alert('אין אפשרויות למפות — כולן כבר מסומנות או יש להן הצעות');
      return;
    }

    setAiRunning(true);
    setAiProgress({ current: 0, total: toProcess.length });

    const batchSize = 8;
    for (let batchStart = 0; batchStart < toProcess.length; batchStart += batchSize) {
      const batch = toProcess.slice(batchStart, batchStart + batchSize);
      setAiProgress({ current: batchStart, total: toProcess.length });

      if (batchStart > 0) await new Promise(r => setTimeout(r, 2000));

      try {
        // Per-row context — each row only sees its item's assigned spaces.
        const rowsList = batch
          .map((r, idx) => {
            const groupStr = r.option_group ? ` (קבוצה: ${r.option_group})` : '';
            const assignedList = r.assigned_space_ids
              .map(id => `  - ${spaceMap[id] || '?'} [id: ${id}]`)
              .join('\n');
            return `${idx + 1}. פריט: "${r.item_name}", אפשרות: "${r.option_name}"${groupStr}
Assigned spaces for this item:
${assignedList}`;
          })
          .join('\n\n');

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 2000,
            messages: [
              {
                role: 'user',
                content: `You are scoping Israeli construction/renovation item options to applicable rooms.

Each row below is an (item, option) pair where the item is already assigned to a specific
set of spaces. Your job is to pick the SUBSET of those assigned spaces where this specific
option makes sense. You may ONLY return space IDs from the "Assigned spaces" list for each
row — never add spaces that aren't already assigned.

Examples:
- "כיסא" (chair) + "כיסא פינת אוכל" (dining chair), item is assigned to [dining, bedroom]
  → return only the dining space id
- "ריצוף" (flooring) + "ריצוף חוץ" (outdoor tile), item is assigned to [balcony, living, porch]
  → return [balcony, porch]
- If the option is universal / applies to ALL of the item's assigned spaces (or you're unsure),
  return an EMPTY array (we treat empty as "all of the item's assigned spaces").

Items to scope:
${rowsList}

Rules:
- row_index matches the number above
- space_ids MUST be a subset of that row's "Assigned spaces" list (use exact ids)
- empty array means "all of the item's assigned spaces" (universal)
- confidence: 0.0–1.0 (0.9+ for obvious, 0.5–0.8 for reasonable)

Respond ONLY as a JSON array, one object per row, in order:
[{"row_index": 1, "space_ids": ["uuid-a"], "confidence": 0.9, "reasoning": "brief Hebrew explanation"}]`,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errText = await response.text();
          console.error(`[OPTION-SPACE AI] API error:`, errText);
          continue;
        }

        const data = await response.json();
        const text = data.content?.[0]?.text || '';

        const jsonMatch = text.match(/\[[\s\S]*\]/);
        if (!jsonMatch) {
          console.error(`[OPTION-SPACE AI] Could not parse response:`, text);
          continue;
        }

        const parsed = JSON.parse(jsonMatch[0]);

        for (const mapping of parsed) {
          const idx = mapping.row_index - 1;
          if (idx < 0 || idx >= batch.length) continue;

          const row = batch[idx];
          const rawIds = Array.isArray(mapping.space_ids) ? mapping.space_ids : [];

          // Validate: every space_id must be one of the item's assigned spaces
          let validIds = rawIds.filter(id => row.assigned_space_ids.includes(id));
          if (rawIds.length !== validIds.length) {
            console.warn(
              `[OPTION-SPACE AI] Dropped out-of-scope space IDs for "${row.item_name} / ${row.option_name}"`,
              { rawIds, assigned: row.assigned_space_ids },
            );
          }

          // If AI returned every assigned space, normalize to empty (= "all assigned")
          if (validIds.length === row.assigned_space_ids.length) {
            validIds = [];
          }

          console.log(
            `[OPTION-SPACE AI] ✓ "${row.item_name} / ${row.option_name}" → ${validIds.length || 'all'} spaces (${mapping.confidence}) — ${mapping.reasoning}`,
          );

          try {
            await upsertSuggestion({
              master_item_id: row.master_item_id,
              option_group: row.option_group || '',
              option_name: row.option_name,
              suggested_space_ids: validIds,
              confidence: mapping.confidence || 0.5,
              ai_reasoning: mapping.reasoning,
              status: 'pending',
            });

            setSuggestions(prev => ({
              ...prev,
              [row.key]: {
                master_item_id: row.master_item_id,
                option_group: row.option_group || '',
                option_name: row.option_name,
                suggested_space_ids: validIds,
                confidence: mapping.confidence || 0.5,
                ai_reasoning: mapping.reasoning,
                status: 'pending',
              },
            }));
          } catch (err) {
            console.error(`Error saving suggestion for ${row.item_name}:`, err);
          }
        }
      } catch (err) {
        console.error('Error processing batch:', err);
      }
    }

    setAiProgress({ current: toProcess.length, total: toProcess.length });
    setAiRunning(false);
  };

  // ─── Render helpers ─────────────────────────────────────────────

  const getConfidenceClass = confidence => {
    if (confidence == null) return '';
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  };

  // ─── Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bb-mapping-page">
        <div className="bb-mapping-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="bb-mapping-page">
      {/* Header */}
      <div className="bb-mapping-header">
        <h1>מיפוי אפשרויות פריטים לחללים</h1>
        <div className="bb-mapping-header-actions">
          <input
            type="text"
            className="bb-mapping-search"
            placeholder="חיפוש פריט או אפשרות..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Progress */}
      <div className="bb-mapping-progress">
        <div className="bb-mapping-progress-bar">
          <div
            className="bb-mapping-progress-fill"
            style={{ width: `${stats.total ? (stats.scoped / stats.total) * 100 : 0}%` }}
          />
        </div>
        <div className="bb-mapping-progress-text">
          {stats.scoped}/{stats.total} מוגבלים לחללים
          {stats.pending > 0 && ` | ${stats.pending} ממתינים לאישור`}
        </div>
      </div>

      {/* AI actions */}
      <div className="api-key-section">
        <span className="api-key-label">
          Claude API: {apiKey ? '✓ מחובר' : '✗ חסר REACT_APP_CLAUDE_API_KEY ב-.env'}
        </span>
        <button
          className="bb-action-btn primary"
          onClick={runAiMapping}
          disabled={aiRunning || saving || !apiKey}
        >
          {aiRunning
            ? `ממפה... (${aiProgress.current}/${aiProgress.total})`
            : `הרץ מיפוי AI (${stats.workRows} חסרים)`}
        </button>
        <button
          className="bb-action-btn approve"
          onClick={handleBatchApprove}
          disabled={aiRunning || saving}
        >
          אשר הכל (ביטחון ≥0.8)
        </button>
        <button
          className="bb-action-btn primary"
          onClick={handleBatchApply}
          disabled={aiRunning || saving}
        >
          {saving ? 'שומר...' : 'החל מאושרים על master_items'}
        </button>
      </div>

      {/* Filters */}
      <div className="bb-mapping-filters">
        {[
          { key: 'work', label: `לעבודה (${stats.workRows})` },
          { key: 'all', label: `הכל (${stats.total})` },
          { key: 'unscoped', label: `ללא הגבלה (${stats.unscoped})` },
          { key: 'pending', label: `ממתינים (${stats.pending})` },
          { key: 'scoped', label: `מוגבלים (${stats.scoped})` },
          { key: 'low-confidence', label: 'ביטחון נמוך' },
        ].map(f => (
          <button
            key={f.key}
            className={`bb-mapping-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bb-mapping-table-container">
        <table className="bb-mapping-table">
          <thead>
            <tr>
              <th>#</th>
              <th>פריט</th>
              <th>קבוצה</th>
              <th>אפשרות</th>
              <th>הצעת AI</th>
              <th>ביטחון</th>
              <th>חללים מותרים</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="bb-mapping-empty">
                  אין אפשרויות להצגה
                </td>
              </tr>
            ) : (
              filteredRows.map((row, idx) => {
                const suggestion = suggestions[row.key];
                // Clamp effective to the item's assigned spaces — drops any stale ids
                const effective = getEffectiveAllowedSpaces(row).filter(id =>
                  row.assigned_space_ids.includes(id),
                );
                const effectiveSet = new Set(effective);

                return (
                  <tr key={row.key}>
                    <td>{idx + 1}</td>
                    <td>
                      <strong>{row.item_name}</strong>
                      {row.is_bundle && (
                        <span className="item-type-badge" style={{ marginInlineStart: 6 }}>
                          חבילה
                        </span>
                      )}
                    </td>
                    <td>{row.option_group || '—'}</td>
                    <td>{row.option_name}</td>
                    <td>
                      <div className="bb-mapping-actions-cell">
                        {suggestion ? (
                          <span>
                            {(suggestion.suggested_space_ids || [])
                              .map(id => spaceMap[id] || '?')
                              .join(', ') || 'כל החללים המשויכים'}
                          </span>
                        ) : (
                          '—'
                        )}
                        {suggestion?.status === 'pending' && (
                          <>
                            <button
                              className="bb-action-btn approve"
                              onClick={() => handleApprove(row)}
                            >
                              אשר
                            </button>
                            <button
                              className="bb-action-btn reject"
                              onClick={() => handleReject(row)}
                            >
                              דחה
                            </button>
                          </>
                        )}
                        {suggestion?.status === 'rejected' && (
                          <span className="status-badge status-rejected">נדחה</span>
                        )}
                        {suggestion?.status === 'approved' && (
                          <span className="status-badge status-approved">אושר</span>
                        )}
                        {suggestion?.status === 'manual' && (
                          <span className="status-badge status-manual">ידני</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {suggestion?.confidence != null ? (
                        <span
                          className={`confidence-badge ${getConfidenceClass(suggestion.confidence)}`}
                        >
                          {(suggestion.confidence * 100).toFixed(0)}%
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 340 }}>
                        <button
                          className={`bb-action-btn ${effective.length === 0 ? 'approve' : ''}`}
                          onClick={() => handleClearSpaces(row)}
                          title="הצג בכל החללים המשויכים לפריט"
                          style={{ fontSize: 11 }}
                        >
                          כל החללים המשויכים
                        </button>
                        {row.assigned_space_ids.map(spId => {
                          const spName = spaceMap[spId] || '?';
                          // When effective is empty, treat as "all assigned selected"
                          const isSelected = effective.length === 0 ? true : effectiveSet.has(spId);
                          return (
                            <button
                              key={spId}
                              className={`bb-action-btn ${isSelected ? 'approve' : ''}`}
                              onClick={() => handleToggleSpace(row, spId)}
                              style={{ fontSize: 11 }}
                            >
                              {spName}
                            </button>
                          );
                        })}
                        {suggestion && (
                          <button
                            className="bb-action-btn reject"
                            onClick={() => handleClear(row)}
                            title="נקה הצעה"
                            style={{ fontSize: 11 }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Utils ─────────────────────────────────────────────────────────

function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

export default ItemOptionSpaceMappingPage;
